import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, MessageSquare, Sparkles, Repeat, Play, Square } from "lucide-react";
import { LOGO_SRC } from "./Brand.jsx";
import { useAuth } from "../lib/auth.jsx";
import api from "../lib/api.js";
import CoachPickerModal from "./CoachPickerModal.jsx";
import { speakWithPersona, stopSpeaking } from "../lib/voice.js";

/**
 * Floating Posture Coach companion — sits in the bottom-right corner of every
 * authenticated page and offers a contextual blurb of advice / encouragement
 * tied to the user's current screen. Collapsible. Persists a dismissed state
 * per session.
 */
const ROUTE_TIPS = {
  home: {
    headline: "Nice flow on Home",
    body: "Three taps to ship a win — Start Walk, Daily Mission, Spin. Build the streak first; pillars follow.",
  },
  wellness: {
    headline: "Read your pillars",
    body: "Your weakest pillar gets you the biggest gains. One supported walk usually lifts Alignment + Stability at once.",
  },
  devices: {
    headline: "Sensors honest",
    body: "Pair before you walk so I can read your shoulder retraction and cervical neutral live — that's how we tune your gait.",
  },
  walk: {
    headline: "Ground up",
    body: "Plant the heel, roll through the arch, push off the big toe. Shoulders back, ribs down, eyes on the horizon.",
  },
  passive: {
    headline: "Tall sit",
    body: "Sit-bones rooted, crown of head lifting. The Back Transformer holds the cue while you focus on the task.",
  },
  training: {
    headline: "Small movements, big leverage",
    body: "Quality over reps. Two minutes of perfect form trains your spine more than ten minutes of slop.",
  },
  checkin: {
    headline: "30 seconds, sharp signal",
    body: "Your check-in tunes the Coach. Honest input = better nudges all day.",
  },
  coach: {
    headline: "I'm right here",
    body: "Ask me anything — gait, hardware, longevity. Voice button works too.",
  },
  progress: {
    headline: "Trends > totals",
    body: "Stability +4% beats any single big day. Look for slope, not spikes.",
  },
  leaderboard: {
    headline: "Climb honestly",
    body: "Friends scope is where it counts — accountability with people who'll actually call you out.",
  },
  duels: {
    headline: "Stakes change behavior",
    body: "Pick a friend slightly above your level. Loss aversion lifts your daily steps more than any pep talk.",
  },
  community: {
    headline: "Tribe over tactics",
    body: "Join one circle that matches your why. Three messages a week is enough to compound.",
  },
  library: {
    headline: "Watch with intent",
    body: "One lesson per day, applied immediately to the next walk, beats binging four in a row.",
  },
  marketplace: {
    headline: "Spend on identity",
    body: "Pick rewards that reinforce who you're becoming — not who you were.",
  },
  badges: {
    headline: "Proof of work",
    body: "Each badge is a deposit in your future self's confidence account.",
  },
  profile: {
    headline: "Tune the engine",
    body: "Pair your Back Transformer here and set your age so I can score Posture Age™ accurately.",
  },
  default: {
    headline: "Stand tall",
    body: "Crown up, shoulders back, ribs over hips. The posture you carry shapes the day that finds you.",
  },
};

function routeKey(pathname) {
  if (pathname === "/" || pathname === "/home") return "home";
  if (pathname.startsWith("/wellness")) return "wellness";
  if (pathname.startsWith("/devices")) return "devices";
  if (pathname.startsWith("/walk")) return "walk";
  if (pathname.startsWith("/passive")) return "passive";
  if (pathname.startsWith("/training")) return "training";
  if (pathname.startsWith("/checkin")) return "checkin";
  if (pathname.startsWith("/coach")) return "coach";
  if (pathname.startsWith("/progress")) return "progress";
  if (pathname.startsWith("/leaderboard")) return "leaderboard";
  if (pathname.startsWith("/duels")) return "duels";
  if (pathname.startsWith("/community")) return "community";
  if (pathname.startsWith("/library")) return "library";
  if (pathname.startsWith("/lesson")) return "library";
  if (pathname.startsWith("/marketplace")) return "marketplace";
  if (pathname.startsWith("/badges")) return "badges";
  if (pathname.startsWith("/profile")) return "profile";
  return "default";
}

const ROUTES_HIDDEN = ["/login", "/register", "/", "/onboarding", "/admin", "/corporate"];

export default function CoachCompanion() {
  const loc = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [nudge, setNudge] = useState(null);
  const [persona, setPersona] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const autoCloseRef = useRef(null);
  const introTimerRef = useRef(null);

  const key = routeKey(loc.pathname);
  const baseTip = ROUTE_TIPS[key] || ROUTE_TIPS.default;

  // Fetch active persona
  useEffect(() => {
    if (!user) return;
    api.get("/coach/personas").then((r) => {
      const sel = r.data.personas.find((p) => p.id === r.data.selected_id) || r.data.personas[0];
      setPersona(sel);
    }).catch(() => {});
  }, [user]);

  // On home, swap the body with the live coach nudge if available.
  useEffect(() => {
    if (!user) { setNudge(null); return; }
    const today = new Date().toISOString().slice(0, 10);
    api.get("/coach/history?limit=10").then((r) => {
      const existing = r.data.find(
        (m) => m.role === "assistant" && m.kind === "nudge" && (m.created_at || "").startsWith(today)
      );
      if (existing) setNudge(existing.text);
      else {
        // Generate today's nudge (idempotent per day server-side)
        api.post("/coach/nudge").then((nr) => setNudge(nr.data.text)).catch(() => {});
      }
    }).catch(() => {});
  }, [user]);

  // Auto-play today's nudge once per day in the persona's voice, on the
  // user's first interaction with the app (any page).
  const [playing, setPlaying] = useState(false);
  const autoplayTriedRef = useRef(false);
  useEffect(() => {
    if (!nudge || !user || autoplayTriedRef.current) return;
    const today = new Date().toISOString().slice(0, 10);
    const key = `ps_nudge_audio_${today}`;
    if (localStorage.getItem(key) === "1") return;
    autoplayTriedRef.current = true;

    let started = false;
    let inflight = false;
    const base = process.env.REACT_APP_BACKEND_URL || "";

    const cleanup = () => {
      window.removeEventListener("pointerdown", gesture, true);
      window.removeEventListener("keydown", gesture, true);
      window.removeEventListener("touchstart", gesture, true);
    };
    const tryPlay = async () => {
      if (started || inflight) return;
      inflight = true;
      await speakWithPersona(nudge, {
        apiBase: base,
        onStart: () => {
          started = true;
          setPlaying(true);
          localStorage.setItem(key, "1");
          cleanup();
        },
        onEnd: () => { setPlaying(false); inflight = false; },
      });
      inflight = false;
    };
    function gesture() { tryPlay(); }

    const t = setTimeout(tryPlay, 800);
    window.addEventListener("pointerdown", gesture, true);
    window.addEventListener("keydown", gesture, true);
    window.addEventListener("touchstart", gesture, true);
    return () => { clearTimeout(t); cleanup(); };
  }, [nudge, user]);

  const togglePlay = (e) => {
    e?.stopPropagation();
    if (playing) { stopSpeaking(); setPlaying(false); return; }
    if (!nudge) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    setPlaying(true);
    speakWithPersona(nudge, { apiBase: base, onEnd: () => setPlaying(false) });
  };

  // Slight intro pop after page mount
  useEffect(() => {
    clearTimeout(introTimerRef.current);
    setOpen(false);
    if (dismissed) return;
    introTimerRef.current = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(introTimerRef.current);
  }, [loc.pathname, dismissed]);

  // Auto-collapse after 9s
  useEffect(() => {
    clearTimeout(autoCloseRef.current);
    if (open) autoCloseRef.current = setTimeout(() => setOpen(false), 9000);
    return () => clearTimeout(autoCloseRef.current);
  }, [open]);

  const tip = useMemo(() => {
    if (key === "home" && nudge) {
      const snippet = nudge.length > 180 ? nudge.slice(0, 175).trimEnd() + "…" : nudge;
      return { headline: "Today's coach nudge", body: snippet };
    }
    return baseTip;
  }, [key, nudge, baseTip]);

  if (!user) return null;
  if (ROUTES_HIDDEN.includes(loc.pathname)) {
    // Show on home even when path is "/" — the protected route renders HomeDash.
    if (loc.pathname !== "/") return null;
  }
  if (dismissed) return null;

  const accent = persona?.accent || "#4ade80";
  const avatarSrc = persona?.avatar_url || LOGO_SRC;
  const coachName = persona?.name || "Posture Coach";
  const coachRole = persona?.role || "Sidelines";

  return (
    <div
      className="fixed z-[70] right-3 sm:right-4 pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }}
      data-testid="coach-companion"
    >
      <div className="flex items-end gap-2 pointer-events-auto justify-end">
        {/* Speech bubble */}
        {open && (
          <div
            className="ps-card-elev p-3.5 max-w-[270px] relative animate-[ps-fade-up_.45s_ease-out_both] order-1"
            style={{ boxShadow: `0 22px 60px -22px ${accent}88, inset 0 1px 0 rgba(255,255,255,0.08)` }}
            data-testid="coach-companion-bubble"
          >
            <button
              onClick={() => setDismissed(true)}
              className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-[#0E1F2A] border border-[#1B364A] flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ef4444]/40"
              data-testid="coach-companion-dismiss"
              aria-label="Dismiss coach companion"
            >
              <X size={12} />
            </button>
            <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>
              <Sparkles size={10} /> {coachName} · <span className="text-slate-400">{coachRole}</span>
            </div>
            <div className="ps-heading text-[15px] font-extrabold mt-1 leading-tight">
              {tip.headline}
            </div>
            <div className="text-[12px] text-slate-300 ps-serif leading-snug mt-1.5">
              {tip.body}
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-2">
              <Link
                to="/coach"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold hover:underline"
                style={{ color: accent }}
                data-testid="coach-companion-open-chat"
              >
                <MessageSquare size={11} /> Talk to coach
              </Link>
              <div className="flex items-center gap-2">
                {nudge && (
                  <button
                    onClick={(e) => { e.preventDefault(); togglePlay(e); }}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold hover:underline"
                    style={{ color: playing ? "#ef4444" : accent }}
                    data-testid="coach-companion-play"
                  >
                    {playing ? <Square size={11} fill="currentColor" /> : <Play size={11} fill="currentColor" />}
                    {playing ? "Stop" : "Play"}
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-white"
                  data-testid="coach-companion-switch"
                >
                  <Repeat size={11} /> Switch
                </button>
              </div>
            </div>
            {/* Bubble tail */}
            <span
              aria-hidden
              className="absolute -right-2 bottom-5 w-0 h-0"
              style={{
                borderTop: "8px solid transparent",
                borderBottom: "8px solid transparent",
                borderLeft: "10px solid rgba(14,31,42,0.95)",
                filter: "drop-shadow(2px 0 4px rgba(0,0,0,0.4))",
              }}
            />
          </div>
        )}

        {/* Coach avatar button */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="relative shrink-0 order-2 rounded-full overflow-visible transition-transform duration-200 hover:scale-105 active:scale-95"
          style={{ width: 64, height: 64 }}
          data-testid="coach-companion-avatar"
          aria-label="Open Coach companion"
        >
          {/* Pulsing aura */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full ps-pulse pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${accent}73 0%, transparent 70%)`,
              filter: "blur(8px)",
            }}
          />
          {/* Avatar disk */}
          <div
            className="relative rounded-full overflow-hidden flex items-center justify-center bg-[#0E1F2A]"
            style={{
              width: 64,
              height: 64,
              boxShadow: `inset 0 2px 0 rgba(255,255,255,0.32), inset 0 -3px 6px rgba(0,0,0,0.4), 0 14px 36px -10px ${accent}b3, 0 2px 0 rgba(0,0,0,0.45)`,
              border: `2px solid ${accent}66`,
            }}
          >
            <img
              src={avatarSrc}
              alt={coachName}
              draggable={false}
              onError={(e) => { e.currentTarget.src = LOGO_SRC; }}
              className="w-full h-full object-cover select-none"
            />
          </div>
          {/* Status dot */}
          {!open && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-[#050E15] animate-pulse"
              style={{ background: "#FBBF24", boxShadow: "0 0 8px #FBBF24" }}
            />
          )}
        </button>
      </div>
      <CoachPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} onSelected={(p) => setPersona(p)} />
    </div>
  );
}
